import React, { useRef, useState } from 'react';
import Papa from 'papaparse';
import {
  Box, Typography, Snackbar, Alert, IconButton, Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';

const CSV_HEADERS = [
  'name', 'address', 'type', 'status', 'price', 'bedrooms', 'bathrooms', 'area', 'lotSize', 'yearBuilt', 'description', 'tags', 'notes',
];

function downloadTemplate() {
  const csv = `${CSV_HEADERS.join(',')}\n`;
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'properties-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function PropertyImport({ onImportSuccess }) {
  const fileInputRef = useRef();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const res = await fetch('/api/v1/properties/import', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(results.data),
          });
          const data = await res.json();
          if (data.success) {
            setSnackbar({ open: true, message: `Imported ${data.inserted} properties!`, severity: 'success' });
            if (onImportSuccess) onImportSuccess();
          } else {
            setSnackbar({ open: true, message: data.error || 'Import failed', severity: 'error' });
          }
        } catch (err) {
          setSnackbar({ open: true, message: 'Import failed', severity: 'error' });
        }
      },
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Tooltip title="Import Properties (CSV)">
          <IconButton
            color="primary"
            onClick={() => fileInputRef.current.click()}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <CloudUploadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download CSV Template">
          <IconButton
            color="primary"
            variant="outlined"
            onClick={downloadTemplate}
            sx={{
              borderColor: 'primary.main',
              '&:hover': { bgcolor: 'primary.light', borderColor: 'primary.dark' },
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <input
        type="file"
        accept=".csv"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        CSV columns should match your property fields. Use the template for best results.
      </Typography>
    </Box>
  );
}
